import type { AnyRouter } from '@tanstack/react-router'
import { CONSENT_DEFAULTS } from '@athena/shared'
import { languageFromPathname } from './language.js'

type AnalyticsWindow = Window & {
  dataLayer?: unknown[]
  gtag?: (...args: unknown[]) => void
}

/** Track resolved screens, never resource IDs, search strings or rendered titles. */
export function startAnalytics(
  router: AnyRouter,
  measurementId: string | undefined = import.meta.env.PROD
    ? import.meta.env.VITE_GA_MEASUREMENT_ID
    : undefined,
): () => void {
  const id = measurementId?.trim()
  if (!id || !/^G-[A-Z0-9]+$/.test(id)) return () => {}

  const analyticsWindow = window as AnalyticsWindow
  let initialized = false
  let lastPathname: string | undefined
  let previousLocation = ''

  return router.subscribe('onResolved', () => {
    const { location, matches } = router.state
    if (location.pathname === lastPathname) return
    lastPathname = location.pathname

    const match = matches.at(-1)
    const language = languageFromPathname(location.pathname)
    const safeLanguage = language === 'en' ? 'en' : 'es'
    const route = match && router.routesById[match.routeId]
    const path =
      !route || matches.some((candidate) => candidate.status === 'notFound' || candidate._notFound)
        ? `/${safeLanguage}/404`
        : route.fullPath.replace('$language', safeLanguage)
    const page = {
      page_location: new URL(path, window.location.origin).href,
      page_title: path,
      page_referrer: previousLocation,
    }

    if (!initialized) {
      analyticsWindow.dataLayer ??= []
      analyticsWindow.gtag = function () {
        // Match Google's gtag snippet: queue native Arguments objects.
        // eslint-disable-next-line prefer-rest-params -- Preserve the vendor's command format.
        analyticsWindow.dataLayer!.push(arguments)
      }
      const gtag = analyticsWindow.gtag
      for (const defaults of CONSENT_DEFAULTS) gtag('consent', 'default', defaults)
      gtag('js', new Date())
      gtag('config', id, {
        ...page,
        send_page_view: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      })
      const script = document.createElement('script')
      script.async = true
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
      document.head.append(script)
      initialized = true
    }

    // Also update defaults used by automatic session/engagement events.
    analyticsWindow.gtag!('set', page)
    analyticsWindow.gtag!('event', 'page_view', { ...page, send_to: id })
    previousLocation = page.page_location
  })
}
